// Typed wrapper around the auth endpoints exposed by the StockScreener API.
//
// Backend contract (Phase 1):
//   POST /api/auth/register  { email, password }       → AuthSuccessResponse | 400 | 409
//   POST /api/auth/login     { email, password }       → AuthSuccessResponse | 401
//   POST /api/auth/refresh   { refreshToken }          → AuthSuccessResponse | 401
//   POST /api/auth/logout    { refreshToken }, Bearer  → 204
//   GET  /api/auth/me        Bearer                    → AuthUser | 401
//
// Note: refresh-on-401 is handled at the apiClient layer, not here. This
// service only deals with the auth endpoints themselves and does not consume
// the bearer for /login, /register, /refresh.

import { API_BASE } from '@/env';
import { apiFetch } from '@/auth/apiClient';

export type AuthRole = 'SuperAdmin' | 'AccountManager' | 'User';
export type AuthTier = 'Free' | 'Basic' | 'Pro' | 'Enterprise';

export interface AuthUser {
  id: string;
  email: string;
  roles: string[]; // server returns string[], typically subset of AuthRole
  tier: string;    // server returns string, typically AuthTier
}

export interface AuthSuccessResponse {
  user: AuthUser;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface AuthErrorResponse {
  error: string;
}

export class AuthError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'AuthError';
  }
}

async function parseError(res: Response, fallback: string): Promise<AuthError> {
  try {
    const body = (await res.json()) as Partial<AuthErrorResponse>;
    return new AuthError(res.status, body?.error || fallback);
  } catch {
    return new AuthError(res.status, fallback);
  }
}

export const authService = {
  async register(email: string, password: string): Promise<AuthSuccessResponse> {
    const res = await fetch(`${API_BASE}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      if (res.status === 409) throw await parseError(res, 'Email is already registered');
      throw await parseError(res, 'Registration failed');
    }
    return (await res.json()) as AuthSuccessResponse;
  },

  async login(email: string, password: string): Promise<AuthSuccessResponse> {
    const res = await fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      if (res.status === 401) throw await parseError(res, 'Invalid email or password');
      throw await parseError(res, 'Login failed');
    }
    return (await res.json()) as AuthSuccessResponse;
  },

  async refresh(refreshToken: string): Promise<AuthSuccessResponse> {
    const res = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) throw await parseError(res, 'Refresh failed');
    return (await res.json()) as AuthSuccessResponse;
  },

  /** Logout requires a bearer access token (apiFetch attaches it automatically). */
  async logout(refreshToken: string): Promise<void> {
    const res = await apiFetch(`${API_BASE}/api/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    // 204 expected; tolerate 200/401 silently — caller will clear local state regardless.
    if (!res.ok && res.status !== 204 && res.status !== 401) {
      throw await parseError(res, 'Logout failed');
    }
  },

  /** Fetch the current user using the stored access token (via apiFetch). */
  async me(): Promise<AuthUser> {
    const res = await apiFetch(`${API_BASE}/api/auth/me`);
    if (!res.ok) throw await parseError(res, 'Not authenticated');
    return (await res.json()) as AuthUser;
  },
};

export default authService;
